import React, { useEffect } from 'react';
import { Grid, TextField, Button } from '@mui/material';
import SecretField from 'components/JobList/CreateJob/SecretField';
import { useModalContext } from './ModalContext';
import {
  SectionCard,
  useIntegrationConfig,
  IntegrationDetailsCard,
  AuthenticationCard,
  EmployeeIdCounterCard,
  EmailNotificationsCard
} from './greenhouseFormShared';

// Same rationale as GreenhouseApplicationForm's useConfigDefaults: backfill config
// sections once on mount without clobbering an existing integration's values.
// authentication.isAdmin is always forced to true - it's not a user-facing choice.
const useConfigDefaults = () => {
  const { formState, setFormState } = useModalContext();
  useEffect(() => {
    const raw = formState.values.config;
    const config = raw ? JSON.parse(raw) : {};
    const withDefaults = {
      lastId: 0,
      pollRange: 0,
      lastPollDate: new Date().toISOString(),
      ...config,
      greenhouseOnboarding: {
        clientId: '',
        clientSecret: '',
        ...config.greenhouseOnboarding
      },
      authentication: {
        ...config.authentication,
        isAdmin: true
      },
      emailConfig: {
        emailContacts: [],
        sendSuccessEmails: 'true',
        greenhouseEmployeeUrl: '',
        ...config.emailConfig
      }
    };
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        config: JSON.stringify(withDefaults, null, 2)
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const GreenhouseOnboardingCredentialsCard = () => {
  const [greenhouseOnboarding, setGreenhouseOnboarding] = useIntegrationConfig([
    'greenhouseOnboarding'
  ]);
  const creds = greenhouseOnboarding || {};
  const setField = (field) => (value) =>
    setGreenhouseOnboarding({ ...creds, [field]: value });

  return (
    <SectionCard
      subheader="Used to poll Greenhouse's Onboarding API for new hires"
      title="Greenhouse Onboarding credentials"
    >
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label="Client ID"
            onChange={(e) => setField('clientId')(e.target.value)}
            required
            value={creds.clientId || ''}
            variant="standard"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <SecretField
            label="Client secret"
            secret={creds.clientSecret ?? ''}
            setState={setField('clientSecret')}
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

const PollingCard = () => {
  const [lastPollDate, setLastPollDate] = useIntegrationConfig([
    'lastPollDate'
  ]);
  const [pollRange, setPollRange] = useIntegrationConfig(['pollRange']);

  return (
    <SectionCard
      subheader="Controls how often and how far back this integration checks Greenhouse for new hires"
      title="Polling"
    >
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            helperText="ISO 8601 timestamp - editing this on a live integration can cause missed or duplicate hires"
            label="Last poll date"
            onChange={(e) => setLastPollDate(e.target.value)}
            value={lastPollDate || ''}
            variant="standard"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            helperText="0 continuously polls everything since the last run. A positive value (ms) processes one backfill chunk of that size and then disables the integration"
            label="Poll range (ms)"
            onChange={(e) =>
              setPollRange(e.target.value === '' ? '' : Number(e.target.value))
            }
            type="number"
            value={pollRange ?? ''}
            variant="standard"
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const GreenhouseOnboardingForm = () => {
  const { onSubmitIntegration, isUpdate } = useModalContext();
  useConfigDefaults();

  return (
    <form onSubmit={onSubmitIntegration}>
      <Grid container direction="column" spacing={3} wrap="nowrap">
        <Grid item>
          <IntegrationDetailsCard />
        </Grid>
        <Grid item>
          <AuthenticationCard />
        </Grid>
        <Grid item>
          <GreenhouseOnboardingCredentialsCard />
        </Grid>
        <Grid item>
          <EmployeeIdCounterCard />
        </Grid>
        <Grid item>
          <EmailNotificationsCard urlPlaceholder="https://onboarding.greenhouse.io/employees/" />
        </Grid>
        <Grid item>
          <PollingCard />
        </Grid>
        <Grid item>
          <Button color="primary" type="submit" variant="contained">
            {isUpdate ? 'Update Integration' : 'Save Integration'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default GreenhouseOnboardingForm;
